import { Button, Checkbox, ClickAwayListener, Grid, Menu, Tooltip } from '@mui/material';
import { CaretRightIcon, InfoIcon } from '@phosphor-icons/react';
import { CaretRight } from '@phosphor-icons/react/dist/ssr';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangePickerWithTime from 'src/pages/report/test';
import Service from 'src/setup/network';
import { oneDayAgoDate, todayDate } from 'src/utils/dateOneMonthAgo';
import '../../../index.css';
import PieChartComp from './PieChartComp';
import { fetchSales } from 'src/redux/action/sales.action';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/reducers';
import { Button as ButtonComponent } from 'src/components';
import { BiChevronDown } from 'react-icons/bi';
import { CurrencyAmount } from 'src/containers/currency';


const AdminDashboard = ({ renderBodyComp }: any) => {
  const dispatch = useDispatch()
  const [data, setData] = useState<any>([]);
  const [startDate, setStartDate] = useState(oneDayAgoDate);
  const [endDate, setEndDate] = useState(todayDate());
  const { salesData } = useSelector(
    ({ salesData }: RootState) => salesData
  );
  const [floors, setFloors] = useState(salesData.floors)
  const [dateQuery, setDateQuery] = useState(
    `from=${startDate?.toISOString()}&to=${endDate?.toISOString()}`
  );
  const [status, setStatus] = useState(salesData.floors);
  const [querySend, setQuerySend] = useState((''))
  const [open, setOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openFloors = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  useEffect(() => {
    const floors = salesData.floors;
    if(floors.length ===0) return
    setFloors(floors);
  }, [salesData]);

  useEffect(() => {
    dispatch(fetchSales() as any);
  }, []);

  const stats = useMemo(() => [
    {
      title: 'Total Customers',
      amount: data.totalCustomers,
    },
    {
      title: 'Gross Sales',
      amount: data.grossSales,
    },
    {
      title: 'Total Discount',
      amount: data.discountTotal,
    },
    {
      title: 'Net Sales',
      amount: data.netSales,
    },
    {
      title: 'Credit Sales',
      amount: data.creditSales,
    },
    {
      title: 'Non-Credit Sales',
      amount: data.nonCreditSales,
    },
    {
      title: 'Credit Received',
      amount: data.creditReceived,
    },
    {
      title: 'Expenses',
      amount: data.expenseTotal,
    },
    {
      title: 'Total Orders',
      amount: data.totalOrders,
    },
    {
      title: 'COGS',
      amount: data.cogs,
      tooltip: true,
      details: 'Cost of Goods Sold'
    },
    {
      title: 'Profit',
      amount: data.profit,
      tooltip: true,
      details: 'Net Sales - COGS'
    },
    {
      title: 'Net Profit',
      amount: data.netProfit,
      tooltip: true,
      details: 'Profit - Expense Total'
    },
  ], [data]);

  const handleTooltipOpen = (index: number) => {
    setOpen(true);
    setOpenIndex(index);
  };

  const handleTooltipClose = (index: number) => {
    if (openIndex === index) {
      setOpen(false);
      setOpenIndex(null);
    }
  };

  const RenderBody = useCallback(() => (
    <div>
      <Grid container spacing={2}>
        {stats?.map((each, i) => (
          <Grid item xs={3}>
            <div
              style={{ boxShadow: 'none' }}
              className="bg-pure-white text-black p-3 rounded-xl"
            >
              <div className="flex flex-col gap-2 items-start">
                {
                  each.tooltip ? 
                  <ClickAwayListener onClickAway={()=>handleTooltipClose(i)}>
                    <div>
                      <Tooltip
                        onClose={()=>handleTooltipClose(i)}
                        open={open && openIndex==i}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={each.details}
                        slotProps={{
                          popper: {
                            disablePortal: true,
                          },
                        }}
                      >
                        <div onClick={()=>handleTooltipOpen(i)} className="flex text-[#9d9ca3] font-medium text-sm items-center gap-2">
                          <span>{each.title}</span> 
                          <InfoIcon size={18} />
                        </div>
                      </Tooltip>
                    </div>
                  </ClickAwayListener>
                  :
                  <span className="text-[#9d9ca3] font-medium text-sm">
                      {each.title}
                  </span>
                }
                <span className="text-black text-2xl">
                  {(i === 0 || i === 8) ? each.amount : <CurrencyAmount amount={each.amount ?? '-'}/>}
                </span>
              </div>
            </div>
          </Grid>
        ))}
      </Grid>
      <div className="flex flex-col xl:flex-row mt-12 gap-6">
        <div
          className="text-black border border-solid border-light-gray rounded"
          style={{
            height: 'fit-content',
            paddingBottom: 16,
            width: '410px',
            backgroundColor: '#fff',
          }}
        >
          <div style={{ padding: '14px 16px' }} className="font-semibold">
            Payment Methods
          </div>
          <div style={{ height: '300px' }}>
            {data?.paymentMethodSummaries?.length === 0 ? (
              <div className="flex justify-center mt-5">
                <span>No data yet</span>
              </div>
            ) : (
              <PieChartComp data={data?.paymentMethodSummaries ?? []} />
            )}
          </div>
        </div>
        <div className='flex gap-6'>
          <div
          className="text-black border border-solid border-light-gray rounded h-fit"
          style={{ width: '330px' }}
        >
          <Button className="bg-white" style={{ textTransform: 'none' }}>
            <span className="text-black">Credit Users</span>
            <CaretRight className="text-black" size={20} />
          </Button>
          <div
            className="grid text-black grid-cols-4 py-2 px-3 font-semibold"
            style={{
              backgroundColor: '#F2F2F2',
            }}
          >
            <div className="flex col-span-3" style={{ gap: 16 }}>
              <span className="ml-1">Name</span>
            </div>
            <span className="col-span-1">Amount</span>
          </div>
          <div
            className="text-black py-2 px-3 bg-pure-white"
            style={{ minHeight: '296px' }}
          >
            {data?.creditUserSummaries?.map((each: any, i: number) => (
              <div
                key={'creditUserSummaries' + i}
                className="grid grid-cols-4"
              >
                <span className="col-span-3">{each.name}</span>
                <span className="col-span-1"><CurrencyAmount amount={each.amount}/></span>
              </div>
            ))}
            {data?.creditUserSummaries?.length === 0 && (
              <div className="flex justify-center mt-5">
                <span>No data yet</span>
              </div>
            )}
          </div>
          </div>
          <div
            className="text-black border border-solid border-light-gray rounded h-fit bg-pure-white"
            style={{ width: '330px' }}
          >
            <Button className="bg-white" style={{ textTransform: 'none' }}>
              <span className="text-black">Credit-Received Users</span>
              <CaretRightIcon className="text-black" size={20} />
            </Button>
            <div
              className="grid text-black grid-cols-4 py-2 px-3 font-semibold"
              style={{
                backgroundColor: '#F2F2F2',
              }}
            >
              <div className="flex col-span-3" style={{ gap: 16 }}>
                <span className="ml-1">Name</span>
              </div>
              <span className="col-span-1">Amount</span>
            </div>
            <div
              className="text-black py-2 px-3"
              style={{ minHeight: '296px' }}
            >
              {data?.creditPaymentUserSummaries?.map((each: any, i: number) => (
                <div className="flex flex-col">
                  <div
                    key={'creditPaymentUserSummaries' + i}
                    className="grid grid-cols-4"
                  >
                    <span className="col-span-3">{each.name}</span>
                    <span className="col-span-1"><CurrencyAmount amount={each.amount}/></span>
                  </div>
                  <div>
                    {each.origins?.map(
                      (origin: { name: string; total: number }) => (
                        <div className="ml-2">
                          {origin.name} - <CurrencyAmount amount={origin.total}/>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))}
              {data?.creditPaymentUserSummaries?.length === 0 && (
                <div className="flex justify-center mt-5">
                  <span>No data yet</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="hidden">{renderBodyComp(startDate, endDate, data)}</div>
    </div>
  ), [
  stats,
  data,
  open,
  openIndex
  ]);

  const allSelected = () => status.length === 0 || status.length === floors.length;

  function removeObjectById(array: any[], id: any) {
    return array.filter(item => item.id !== id);
  }

  const handleCheckbox = (event: any, i: number|string) => {
    event.stopPropagation();
    if (i === 'all') {
      if (status.length === floors.length) return setStatus([]);
      setStatus(floors);
      return;
    }
    const selected = floors?.[i];
    if (!selected) return;
    let statuses: any = [...status];
    statuses = removeObjectById(status, 1);
    const alreadyPresent = status.find(
      (x: any) => x?.id === selected?.id
    );
    if (alreadyPresent) {
      setStatus(removeObjectById(statuses, alreadyPresent.id));
      return;
    }
    statuses.push({
      id: selected?.id,
      name: selected?.name
    });
    if (statuses.length === floors.length) statuses = floors;
    setStatus(statuses);
  };

  const RenderFloors = useCallback(() => (
    <>
      <ButtonComponent
        text={
          <div className="flex items-center justify-between w-[160px]">
            <span className="line-clamp-1">
              {allSelected()
                ? 'All'
                : status
                    ?.filter((item: any) => item.id !== 1)
                    .map((item: any) => item.name)
                    .join(', ')}
            </span>
            <BiChevronDown size={18} className="min-w-6" />
          </div>
        }
        aria-controls={openFloors ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openFloors ? 'true' : undefined}
        // @ts-ignore
        onClick={handleClick}
      />
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openFloors}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          style: {
            boxShadow: 'none',
            marginTop: '5px',
            border: '1px solid #4062FF',
            height: 'fit-content',
            backgroundColor: '#FFFFFF',
            overflowY: 'auto',
          },
        }}
      >
        <div
            key={'payment' + 'all'}
            className={`font-Outfit px-2 py-2 hover:bg-almost-white font-normal text-[14px] leading-6  cursor-pointer `}
          >
            <Checkbox
              checked={status.length === floors.length}
              onClick={(e) => handleCheckbox(e, 'all')}
            />
            All
          </div>
        {floors?.map((each: any, i: number) => (
          <div
            key={'payment' + i}
            className={`font-Outfit px-2 py-2 hover:bg-almost-white font-normal text-[14px] leading-6  cursor-pointer `}
          >
            <Checkbox
              checked={
                status?.findIndex(
                  (x: any) => x?.id === each.id
                ) >= 0
              }
              onClick={(e) => handleCheckbox(e, i)}
            />
            {each.name}
          </div>
        ))}
      </Menu>
    </>
  ), [
  floors,
  status,
  anchorEl
]);

  useEffect(()=>{
    if(floors.length>0) fetchData()
  },[querySend, status, floors])

  const updateQuery = useCallback(async (query: any) => {
    setQuerySend(query);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const floorsQuery = `?floors=${status.map((f: any) => f.id).join(',')}`;
      const query = querySend + floorsQuery
      const res = await Service.get(
        `/payments/receipt/sales/summary${query}`
      );
      const data = (await res?.data) ?? [];
      setData(data);
    } catch (err) {}
  }, [querySend, status]);

  return (
    <>
      <div className="pb-10 flex flex-col">
        <div className="flex items-center ml-auto mt-4 gap-2">
          {RenderFloors()}
          {DateRangePickerWithTime(
            updateQuery,
            [],
            setDateQuery,
            setStartDate,
            setEndDate
          )}
        </div>
        <div className="mt-5">{RenderBody()}</div>
      </div>
    </>
  );
};

export default AdminDashboard;
